/**
 * eBay marketplaces the product covers. Keep in step with
 * `apps/scrapers/src/scrapers/ebay/countries.ts`, apps do not share a package,
 * so both lists must be updated together when a market is certified public.
 *
 * Only end-to-end verified markets are listed here (public API enum).
 * Currency is derived from country, never picked separately, the marketplace
 * decides the host, the proxy exit and the currency together.
 *
 * See apps/scrapers/src/scrapers/ebay/README.md, "Markets and currency".
 */

export const EBAY_COUNTRY_CODES = ['US', 'UK'] as const;

export type EbayCountryCode = (typeof EBAY_COUNTRY_CODES)[number];

export const DEFAULT_EBAY_COUNTRY: EbayCountryCode = 'US';

export type EbayCurrency = 'USD' | 'GBP';

export type EbayCountryOption = {
    code: EbayCountryCode;
    name: string;
    /** Expected ISO 4217 for this country pool. */
    currency: EbayCurrency;
};

export const EBAY_COUNTRIES: EbayCountryOption[] = [
    { code: 'US', name: 'United States', currency: 'USD' },
    { code: 'UK', name: 'United Kingdom', currency: 'GBP' },
];

/** Seller location relative to marketplace `country` (LH_PrefLoc). */
export const ITEM_LOCATION_CODES = ['country', 'available', 'worldwide'] as const;

export type ItemLocationCode = (typeof ITEM_LOCATION_CODES)[number];

export const DEFAULT_ITEM_LOCATION: ItemLocationCode = 'worldwide';

/**
 * Option labels name what eBay filters, inventory location vs shipping reach.
 * Vague phrases like "any origin" read as the same as both other options.
 */
export function itemLocationOptions(
    market: EbayCountryCode = DEFAULT_EBAY_COUNTRY,
): { code: ItemLocationCode; label: string }[] {
    return [
        {
            code: 'worldwide',
            label: `Worldwide`,
        },
        {
            code: 'country',
            label: `Stock located in ${market}`,
        },
        {
            code: 'available',
            label: `Stock shipped into ${market}`,
        },
    ];
}

const BY_CODE = Object.fromEntries(EBAY_COUNTRIES.map((c) => [c.code, c])) as Record<
    EbayCountryCode,
    EbayCountryOption
>;

export function isEbayCountryCode(value: string): value is EbayCountryCode {
    return (EBAY_COUNTRY_CODES as readonly string[]).includes(value);
}

export function isItemLocationCode(value: string): value is ItemLocationCode {
    return (ITEM_LOCATION_CODES as readonly string[]).includes(value);
}

export function getEbayCountry(code: EbayCountryCode = DEFAULT_EBAY_COUNTRY): EbayCountryOption {
    return BY_CODE[code];
}

export function getExpectedCurrency(code: EbayCountryCode = DEFAULT_EBAY_COUNTRY): EbayCurrency {
    return getEbayCountry(code).currency;
}
